/**
 * navFilter — rights-filtered navigation (analytics platform Phase-2 item 6).
 *
 * Nav nodes in application.json may carry an optional `roles: string[]`
 * allow-list (written by the platform's nav editor, `nav_edit.py`). A node
 * whose `roles` is a non-empty array is shown only to callers holding at
 * least one of those roles (case-insensitive). An absent/empty/malformed
 * `roles` value means "visible to everyone" — fully backward compatible.
 *
 * A node whose `obj_name` appears in `options.grantedObjects` is visible
 * whatever its `roles` list says, and so are the folders above it. That list is
 * a design-time SNAPSHOT of the grants; the grants table is the same statement
 * kept current, and the only one an administrator can edit from inside the
 * running app. A role created after the app was generated can never appear in
 * the snapshot, so granting it a screen worked everywhere except the menu.
 *
 * A node is ALSO hidden when its `obj_name` appears in `options.deniedObjects`
 * — objects the caller holds no OBJECT GRANT on, supplied by the surface
 * (`runtime_rights.denied_objects`). Two independent allow-lists decide what an
 * end user sees, and only one of them is editable from inside the running
 * application: the node's `roles` list belongs to the designer's menu editor,
 * while the Access screen edits the object grant. Reading only the first meant
 * that removing a role's grant correctly stopped it LOADING anything from the
 * object and left the screen in its menu, opening to a permission error — which
 * from the outside is indistinguishable from the removal not having worked.
 * Absent/empty ⇒ nothing extra is hidden, so every existing caller is unchanged.
 *
 * With `options.requireGrant` the rule becomes DENY BY DEFAULT, matching what
 * the endpoints enforce (`twasta_runtime_server.authz.authorize_action`): a
 * node opening ANY object the caller holds no grant on is hidden, not only one
 * that other roles are granted. `grantedObjects` still wins over the `roles`
 * snapshot, so a single grant decides both the menu and the data and a screen
 * is offered exactly when it opens.
 *
 * An APPLICATION ADMINISTRATOR sees every node: a caller holding `admin` or
 * `func-admin`, or one the surface flags via `options.isAdmin` (the platform
 * Run view passes the project owner / FUNC-ADMIN flag from
 * `/site-auth/run-identity`). Without this, the person who OWNS the app opens
 * their own Run view — holding no functional role — and finds every gated
 * screen missing, with nothing on screen to say why. The bypass costs no
 * security: filtering is UX only (see below), and an admin can already reach
 * every object directly.
 *
 * IMPORTANT: nav filtering is UX ONLY — it hides entries from the Run
 * navigation tree. Object-level rights (runtime authz grants / platform
 * project permissions) remain the security boundary; a hidden nav node does
 * NOT block direct access to the underlying object.
 *
 * This file is the SOURCE OF TRUTH for the semantics. The python twin used
 * by the standalone runtime server's `GET /navigation` lives at
 * `packages/runtime-engine/twasta_runtime_engine/nav_filter.py` — keep the
 * two in sync.
 */

/** Structural shape of a flat nav node (application.json `navigation` array —
 *  the shape every Run consumer uses: `RunNavItem` in the platform Run view,
 *  the runtime client's RunView, and the runtime server's `/navigation`). */
export interface RoleFilterableNavNode {
  id: string
  /** The object this node opens; '' / undefined for folders. Matched against
   *  `deniedObjects`. */
  obj_name?: string
  /** '' / undefined for roots (legacy files point roots at the application
   *  id, which simply never appears as a node id — treated as a root). */
  parent_id?: string
  /** '-' = folder; anything else is a runnable object. */
  obj_type?: string
  /** Optional allow-list. Typed `unknown` on purpose: hand-edited files may
   *  carry anything — malformed values are ignored (node stays visible). */
  roles?: unknown
}

/** Roles that confer application-administrator sight of the whole nav tree.
 *  Mirrors the runtime's admin tier (`{admin, func-admin}` — see
 *  `runtime/server/twasta_runtime_server/routers/model_overlay.py`). */
const ADMIN_ROLES = ['admin', 'func-admin']

/** Case-insensitive normalisation for a role list; non-string / blank
 *  entries are dropped, never thrown on. */
function normalizeRoles(roles: unknown): string[] {
  if (!Array.isArray(roles)) return []
  const out: string[] = []
  for (const r of roles) {
    if (typeof r !== 'string') continue
    const v = r.trim().toLowerCase()
    if (v) out.push(v)
  }
  return out
}

/** Whether the node ITSELF is visible to a caller holding `callerRoles`
 *  (ancestors are handled by the tree walk in `filterNavByRoles`). */
function nodeAllowed(
  node: RoleFilterableNavNode, callerRoles: Set<string>, isAdmin: boolean,
  denied?: Set<string> | null, granted?: Set<string> | null,
  carriesGrant?: Set<string> | null, requireGrant?: boolean,
): boolean {
  if (isAdmin) return true // an administrator sees every gated node
  const obj = typeof node?.obj_name === 'string' ? node.obj_name.trim() : ''
  // Deny by default: with `requireGrant`, a node opening an object no grant
  // gives this caller is hidden — the endpoints refuse it, so the menu stops
  // offering it.
  //
  // RUNNABLE nodes only. A folder is not an object and is never granted one,
  // but it is not true that folders carry no `obj_name` — the generator names
  // plenty of them (`emponb_master_data_group`), and hiding a folder here would
  // take its whole subtree with it, including the very children a grant just
  // opened. Folders are pruned by pass 2 once nothing runnable survives inside
  // them, which is the same answer arrived at safely.
  if (requireGrant && obj && node?.obj_type !== '-'
    && !(granted && granted.has(obj))) return false
  // An object that IS grant-controlled and names none of the caller's roles.
  // Redundant under `requireGrant` and the whole rule without it.
  if (obj && denied && denied.has(obj)) return false
  // An object the caller IS granted, or a folder on the way to one: the live
  // grant wins over the design-time `roles` snapshot, so a grant is never
  // unreachable because the menu was written before the role existed.
  if (obj && granted && granted.has(obj)) return true
  if (node?.id && carriesGrant && carriesGrant.has(node.id)) return true
  // Malformed (non-array / no valid string entries) or empty ⇒ visible to all.
  const allow = normalizeRoles(node?.roles)
  if (allow.length === 0) return true
  return allow.some((r) => callerRoles.has(r))
}

/** Options for {@link filterNavByRoles}. */
export interface NavRoleFilterOptions {
  /** Caller is an application administrator (project owner / runtime admin)
   *  by a route this list of roles doesn't express. `true` ⇒ no node is
   *  hidden. Holding `admin` / `func-admin` in `callerRoles` does the same
   *  without the flag. */
  isAdmin?: boolean
  /** Object names the caller holds no grant on — a node opening one of these
   *  is hidden. Absent/empty ⇒ nothing extra is hidden. */
  deniedObjects?: readonly string[] | null
  /** Object names a grant DOES give the caller — shown whatever the node's
   *  `roles` snapshot says. Absent/empty ⇒ nothing extra is shown. */
  grantedObjects?: readonly string[] | null
  /** Apply the runtime's DENY-BY-DEFAULT rule: hide every node whose
   *  `obj_name` is not in `grantedObjects`, not just ones other roles are
   *  granted. Matches what the endpoints enforce
   *  (`twasta_runtime_server.authz.authorize_action`).
   *
   *  A flag rather than the unconditional default because it is only sound for
   *  a caller that actually consulted the grants store: passing it with an
   *  empty `grantedObjects` hides the whole menu, which is correct for "this
   *  caller holds nothing" and wrong for "nobody looked". */
  requireGrant?: boolean
}

/**
 * Filter a FLAT navigation list (nodes linked by `parent_id`) down to what
 * the caller may see:
 *
 *  0. An administrator — `options.isAdmin`, or `admin` / `func-admin` in
 *     `callerRoles` — skips step 1 entirely and sees every node. Step 2
 *     still runs, so a genuinely childless folder is still pruned.
 *  1. Drop every node whose `roles` allow-list is non-empty and shares no
 *     role (case-insensitive) with `callerRoles`, and every node whose
 *     `obj_name` is in `options.deniedObjects`. With `options.requireGrant`,
 *     also drop every node whose `obj_name` is NOT in
 *     `options.grantedObjects` — the runtime's deny-by-default rule. A node
 *     whose `obj_name` is in `options.grantedObjects` is kept regardless, as
 *     are its ancestors — plus their whole subtree
 *     (children of a hidden node are unreachable in the tree anyway).
 *  2. Prune folder nodes (obj_type '-') left with no visible runnable
 *     descendant, so the tree never shows empty folders.
 *
 * Original array order is preserved; input is never mutated. Graceful on
 * malformed data (bad `roles` values ⇒ node stays visible; cycles in
 * `parent_id` don't hang).
 */
export function filterNavByRoles<T extends RoleFilterableNavNode>(
  nav: readonly T[],
  callerRoles: readonly unknown[] | null | undefined,
  options?: NavRoleFilterOptions | null,
): T[] {
  if (!Array.isArray(nav) || nav.length === 0) return []
  const caller = new Set(normalizeRoles(callerRoles as unknown))
  const isAdmin = options?.isAdmin === true || ADMIN_ROLES.some((r) => caller.has(r))
  const nameSet = (v: unknown): Set<string> | null => (
    Array.isArray(v)
      ? new Set(
        (v as readonly unknown[])
          .filter((o): o is string => typeof o === 'string')
          .map((o) => o.trim())
          .filter(Boolean),
      )
      : null
  )
  const denied = nameSet(options?.deniedObjects)
  const granted = nameSet(options?.grantedObjects)
  const requireGrant = options?.requireGrant === true

  const byId = new Map<string, T>()
  for (const n of nav) {
    if (n && typeof n === 'object' && n.id) byId.set(n.id, n)
  }

  // Folders on the way to a granted node. A gated folder would otherwise hide
  // the very screen a grant just opened, which is the same failure one level
  // up. Safe: pass 2 still prunes the folder unless something runnable
  // survives inside it, so it appears only when it holds something usable.
  const carriesGrant = new Set<string>()
  if (granted) {
    for (const n of nav) {
      if (!n || typeof n !== 'object') continue
      const obj = typeof n.obj_name === 'string' ? n.obj_name.trim() : ''
      if (!obj || !granted.has(obj)) continue
      const seenUp = new Set<string>()
      let pid = n.parent_id || ''
      while (pid && !seenUp.has(pid)) {
        seenUp.add(pid)
        carriesGrant.add(pid)
        pid = byId.get(pid)?.parent_id || ''
      }
    }
  }

  // Pass 1 — a node survives when it AND every ancestor (that exists in the
  // list) is allowed. Parents outside the list (legacy application-id roots)
  // are simply where the walk stops.
  const visibleMemo = new Map<string, boolean>()
  const isVisible = (node: T): boolean => {
    const cached = node.id ? visibleMemo.get(node.id) : undefined
    if (cached !== undefined) return cached
    if (node.id) visibleMemo.set(node.id, false) // cycle guard: a parent_id loop reads as hidden mid-walk
    let ok = nodeAllowed(node, caller, isAdmin, denied, granted, carriesGrant,
      requireGrant)
    if (ok) {
      const parent = node.parent_id ? byId.get(node.parent_id) : undefined
      if (parent && parent !== node) ok = isVisible(parent)
    }
    if (node.id) visibleMemo.set(node.id, ok)
    return ok
  }
  const visible = nav.filter((n) => n && typeof n === 'object' && isVisible(n))

  // Pass 2 — prune folders with no visible runnable descendant.
  const childIds = new Map<string, string[]>()
  for (const n of visible) {
    if (!n.id) continue // id-less nodes can't be looked up as children
    const pid = n.parent_id || ''
    if (!childIds.has(pid)) childIds.set(pid, [])
    childIds.get(pid)!.push(n.id)
  }
  const visibleById = new Map(visible.filter((n) => !!n.id).map((n) => [n.id, n]))
  const runnableMemo = new Map<string, boolean>()
  const folderHasRunnable = (folderId: string): boolean => {
    const cached = runnableMemo.get(folderId)
    if (cached !== undefined) return cached
    runnableMemo.set(folderId, false) // cycle guard
    const ok = (childIds.get(folderId) || []).some((cid) => {
      const child = visibleById.get(cid)
      if (!child) return false
      return child.obj_type !== '-' || folderHasRunnable(child.id)
    })
    runnableMemo.set(folderId, ok)
    return ok
  }
  // Folders keep their row only when a visible runnable descendant remains
  // (an id-less folder can never have children — always pruned).
  return visible.filter((n) => n.obj_type !== '-' || (!!n.id && folderHasRunnable(n.id)))
}
